// playbackStore - record/replay harness for the dashboard.
//
// RECORD: polls the live stores every REC_CADENCE_MS, snapshots the slices
// widgets consume into frames, checkpoints them to IndexedDB, then downloads
// them as a JSON fixture on Stop & Save.
// REPLAY: loads minute snapshots and derives cumulative series by timestamp
// cutoff, so a scrub never rebuilds state by replaying earlier frames.

import { create } from 'zustand';
import { createStore } from 'zustand/vanilla';
import {
  buildChainSnapshot,
  chainSnapshotMinuteEnd,
  isSameMinuteCoverageRegression,
  mergeChainSnapshotSeries,
  replaceSameMinuteChainSnapshot,
  stampImportedChainSnapshot,
  upgradeLegacySplitCells,
  useOptionsStore,
} from './optionsStore';
import useGreeksWebsocketDataStore, { createGreeksState } from './GreeksWebsocketDataStore';
import useWebSocketDataStore from './WebSocketDataStore';
import useMarketIntelligenceStore from './marketIntelligenceStore';
import { useChartTimeWindowStore } from './chartTimeWindowStore';
import { useChartPriceWindowStore } from './chartPriceWindowStore';
import {
  clearPlaybackCheckpoint,
  loadPlaybackCheckpoint,
  savePlaybackCheckpoint,
} from './playbackCheckpointStore';
import {
  loadReplayDatasetSession,
  loadPersistedReplayState,
  loadReplayHistory,
  persistReplayDatasetSession,
  persistReplaySnapshot,
  persistReplaySnapshots,
  persistReplayState,
  replayDatasetSessionHasCompatibleProvenance,
  replayDatasetSessionIsStructurallyValid,
  replayDatasetSessionMetadataMatchesDiscovery,
  replayDatasetSessionMatchesCoverage,
} from './playbackHistoryStore';
import {
  replayDatasetCanProject,
  supplementReplayCoverageWithLocalDatasets,
  mergeReplayDatasets,
  normalizeLocalReplayDatasets,
  projectReplayDatasets,
} from '../Utils/replayV2';
import { trimPlaybackFixture } from '../Utils/playbackFixtureTrim.mjs';
import { sanitizeTimeSeriesBars } from '../Utils/barSeries';
import {
  etDateKey,
  isDuringMarketHours,
  latestTradeDayOnly,
  mostRecentSessionDate,
  sessionCloseEpochMs,
} from '../Utils/marketHours';
import {
  countReplayGaps,
  lastFrameIndexBefore,
  mergeRecoveredReplayFrames,
  recoveredApiFrameToBufferFrame,
} from '../Utils/replayGaps';

const REC_CADENCE_MS = 15_000;
const BUFFER_MINUTE_MS = 60_000;
let checkedFramesBySnapshot = new WeakMap();
// Replay sources are immutable; a new source identity invalidates cached MI posts.
const replayMiPostsBySource = new WeakMap();
const FIXTURE_VERSION = 1;
const INDEX_SYMBOLS = ['SPX', 'ES'];
const pendingOptionRows = new Map();
const pendingAggPatches = new Map();
const pendingMiSnapshots = [];
let pendingLiveSpot = null;
let pendingGreeksScratch = null;

function clearPendingLiveQueues() {
  pendingOptionRows.clear();
  pendingAggPatches.clear();
  pendingMiSnapshots.length = 0;
  pendingLiveSpot = null;
  pendingGreeksScratch = null;
}

function replayHistoryHydrationRequestKey(tradingDate, discovery, loadDatasetRecords) {
  return JSON.stringify({
    tradingDate: tradingDate || null,
    loadDatasetRecords: Boolean(loadDatasetRecords),
    discovery: discovery ? {
      tradingDate: discovery.tradingDate || null,
      generatedAt: discovery.generatedAt || null,
      schemaVersion: discovery.schemaVersion || null,
      session: discovery.session || null,
      coverage: discovery.coverage || null,
    } : null,
  });
}

function initializePendingGreeksScratch(liveGreeks) {
  pendingGreeksScratch = createStore(createGreeksState);
  pendingGreeksScratch.setState(JSON.parse(JSON.stringify(liveGreeks || {})));
}

function getGreeksDataState(store) {
  if (!store) return null;
  const state = store.getState();
  return {
    greekExposures: state.greekExposures,
    topOptions: state.topOptions,
    dynamicLevels: state.dynamicLevels,
    gexTrendStrength: state.gexTrendStrength,
    adaptiveTopOptions: state.adaptiveTopOptions,
    adaptiveDynamicLevels: state.adaptiveDynamicLevels,
    lastValidData: state.lastValidData,
  };
}

function applyGreeksOp(store, type, payload) {
  if (!store) return;
  const greeks = store.getState();
  if (type === 'gexTrend') greeks.updateGexTrendStrength('spx', payload);
  if (type === 'greekExposures') greeks.updateGreekExposures('spx', payload);
  if (type === 'topOptions') greeks.updateTopOptions('spx', payload);
  if (type === 'dynamicLevels') greeks.updateDynamicLevels('spx', payload);
}

function sanitizeIndexData(indexData = {}) {
  const out = {};
  for (const [symbol, bars] of Object.entries(indexData || {})) {
    out[symbol] = sanitizeTimeSeriesBars(bars);
  }
  return out;
}

function mergeIndexData(base = {}, incoming = {}) {
  const merged = {};
  for (const symbol of new Set([...Object.keys(base || {}), ...Object.keys(incoming || {})])) {
    merged[symbol] = sanitizeTimeSeriesBars([
      ...(base?.[symbol] || []),
      ...(incoming?.[symbol] || []),
    ]);
  }
  return merged;
}

function indexDataMatches(left = {}, right = {}) {
  const symbols = new Set([
    ...Object.keys(left || {}),
    ...Object.keys(right || {}),
  ]);
  return [...symbols].every((symbol) => {
    const leftBars = left?.[symbol] ?? [];
    const rightBars = right?.[symbol] ?? [];
    if (!Array.isArray(leftBars) || !Array.isArray(rightBars)) return false;
    if (leftBars.length !== rightBars.length) return false;
    return leftBars.every((bar, barIndex) => {
      const other = rightBars[barIndex];
      return bar.time === other?.time
        && bar.open === other?.open
        && bar.high === other?.high
        && bar.low === other?.low
        && bar.close === other?.close;
    });
  });
}

function mergeReplayMiPosts(base = [], incoming = [], cutoffMs) {
  const posts = new Map();
  for (const post of [...base, ...incoming]) {
    const timestamp = itemTimestampMs(post);
    if (!post?.id || timestamp == null || timestamp > cutoffMs) continue;
    posts.set(String(post.id), post);
  }
  return [...posts.values()].sort((left, right) => itemTimestampMs(left) - itemTimestampMs(right));
}

function replayMiPostsAtCutoff(replaySource, cutoffMs) {
  let cached = replayMiPostsBySource.get(replaySource);
  if (!cached) {
    const server = replaySource.datasets?.mi || [];
    const local = normalizeLocalReplayDatasets({
      tradingDate: replaySource.tradingDate,
      miPosts: replaySource.miPosts,
    }).mi || [];
    cached = {
      server,
      local,
      eventTimes: [...server, ...local].map(itemTimestampMs).filter((t) => t != null),
      latestVisibleT: null,
      posts: [],
    };
    replayMiPostsBySource.set(replaySource, cached);
  }
  const latestVisibleT = cached.eventTimes.reduce(
    (latest, t) => (t <= cutoffMs ? Math.max(latest, t) : latest),
    -Infinity,
  );
  if (latestVisibleT !== cached.latestVisibleT) {
    cached.posts = mergeReplayMiPosts(cached.server, cached.local, cutoffMs);
    cached.latestVisibleT = latestVisibleT;
  }
  return cached.posts;
}

function sanitizeAggregationState(aggregationState = {}) {
  const out = {};
  for (const [symbol, state] of Object.entries(aggregationState || {})) {
    if (state && typeof state === 'object') out[symbol] = { ...state };
  }
  return out;
}

function pickIndexData(indexData = {}) {
  const out = {};
  for (const symbol of INDEX_SYMBOLS) {
    out[symbol] = indexData?.[symbol] ?? [];
  }
  return out;
}

function pickAggregationState(aggregationState = {}) {
  const out = {};
  for (const symbol of INDEX_SYMBOLS) {
    const state = aggregationState?.[symbol];
    if (state && typeof state === 'object') out[symbol] = state;
  }
  return out;
}

// Number(null) is 0 and Number('') is 0, both of which Number.isFinite accepts.
// isValidFrame leans on this to reject a frame captured before any live data
// landed (spot is null until the first WSS row), so the coercion has to reject
// the empty-ish values rather than silently scoring them as 0.
function isFiniteNumber(value) {
  if (value === null || value === undefined || value === '' || typeof value === 'boolean') {
    return false;
  }
  return Number.isFinite(Number(value));
}

function isValidFrame(frame) {
  return Boolean(
    frame
    && isFiniteNumber(frame.t)
    && frame.options
    && typeof frame.options === 'object'
    && frame.options.rowsBySym
    && typeof frame.options.rowsBySym === 'object'
    && (isFiniteNumber(frame.options.spot)
      || (frame.datasetAnchor === true && frame.options.spot == null))
    && frame.greeks
    && typeof frame.greeks === 'object'
  );
}

function captureFrame() {
  const o = useOptionsStore.getState();
  const g = useGreeksWebsocketDataStore.getState();
  const w = useWebSocketDataStore.getState();
  const frame = {
    t: Date.now(),
    options: { rowsBySym: o.rowsBySym, spot: o.spot },
    greeks: {
      greekExposures: g.greekExposures,
      dynamicLevels: g.dynamicLevels,
      gexTrendStrength: g.gexTrendStrength,
      topOptions: g.topOptions,
      adaptiveTopOptions: g.adaptiveTopOptions,
      adaptiveDynamicLevels: g.adaptiveDynamicLevels,
    },
    indexData: pickIndexData(w.indexData),
    aggregationState: pickAggregationState(w.aggregationState),
  };
  return JSON.parse(JSON.stringify(frame));
}

function captureBufferFrame({ options, greeks, aggregationState } = {}) {
  const o = options || useOptionsStore.getState();
  const g = greeks || useGreeksWebsocketDataStore.getState();
  const resolvedAggregationState = aggregationState || useWebSocketDataStore.getState().aggregationState;
  return JSON.parse(JSON.stringify({
    t: Date.now(),
    options: { rowsBySym: o.rowsBySym, spot: o.spot },
    greeks: {
      dynamicLevels: g.dynamicLevels,
      topOptions: g.topOptions,
    },
    aggregationState: pickAggregationState(resolvedAggregationState),
  }));
}

function historySnapshotToFrame(snapshot) {
  return {
    t: snapshot.t,
    options: { rowsBySym: snapshot.rowsBySym, spot: snapshot.spot },
    greeks: {
      dynamicLevels: snapshot.dynamicLevels || {},
      topOptions: snapshot.topOptions || {},
    },
    aggregationState: snapshot.aggregationState || {},
    ...(snapshot.datasetAnchor === true ? { datasetAnchor: true } : {}),
    ...(snapshot.partial === true ? { partial: true } : {}),
  };
}

function replayChainRecordsFromFrames(frames = []) {
  return frames.flatMap((frame) => (
    frame?.datasetAnchor !== true && Number.isFinite(frame?.t) && frame?.options?.rowsBySym
      ? [{
        t: frame.t,
        spot: Number.isFinite(frame.options.spot) ? frame.options.spot : null,
        rowsBySym: frame.options.rowsBySym,
      }]
      : []
  ));
}

function replayDatasetAnchorFrames(datasets = {}) {
  const timestamps = new Set();
  for (const records of Object.values(datasets)) {
    for (const record of records || []) {
      const timestamp = record?.t ?? record?.time;
      if (Number.isFinite(timestamp)) timestamps.add(timestamp);
    }
  }
  return [...timestamps].sort((left, right) => left - right).map((t) => ({
    t,
    options: { rowsBySym: {}, spot: null },
    greeks: { dynamicLevels: {}, topOptions: {} },
    aggregationState: {},
    datasetAnchor: true,
    partial: true,
  }));
}

function replayDatasetFrames(datasetSession) {
  const chainFrames = (datasetSession?.datasets?.chain || [])
    .map(recoveredApiFrameToBufferFrame)
    .filter(Boolean);
  return chainFrames.length
    ? chainFrames
    : replayDatasetAnchorFrames(datasetSession?.datasets);
}

function replayCoverageDatasets(datasetSession, frames) {
  return mergeReplayDatasets(datasetSession?.datasets, {
    chain: replayChainRecordsFromFrames(frames),
  });
}

function filterReplaySessionFrames(frames) {
  return (frames || []).filter((frame) => isDuringMarketHours(frame.t));
}

function isLegacyLegSplitSnapshot(snapshot) {
  return Object.values(snapshot?.byStrike || {}).some((cell) => (
    cell?.hasLegSplit !== true
  ));
}

function isReplayCoverageRegression(previous, next) {
  if (!Number.isFinite(next.hydrationFrom) || !Number.isFinite(next.hydrationTo)) {
    return isSameMinuteCoverageRegression(previous, next);
  }
  return Object.entries(previous.byStrike || {}).some(([strike, cell]) => {
    const price = Number(strike);
    if (price < next.hydrationFrom || price > next.hydrationTo) return false;
    const nextCell = next.byStrike?.[strike];
    return !nextCell || (Number(nextCell.vol) || 0) < (Number(cell.vol) || 0);
  });
}

function mergeReplaySameMinuteSnapshot(previous, snapshot, { legacy = false } = {}) {
  const imported = !Number.isFinite(previous.sourceT) || previous.serviceSnapshotOverlay;
  const minuteEnd = chainSnapshotMinuteEnd(snapshot.t);
  const coverageRegression = isReplayCoverageRegression(previous, snapshot);
  const upgraded = legacy
    ? upgradeLegacySplitCells(previous, snapshot, { allowReplay: true })
    : null;
  const splitOnlyRefresh = upgraded && previous.spot === snapshot.spot
    && Object.entries(snapshot.byStrike || {}).every(([strike, nextCell]) => {
      const previousCell = previous.byStrike?.[strike];
      return !previousCell || (
        Number(nextCell.nv) === Number(previousCell.nv)
        && Number(nextCell.vol) === Number(previousCell.vol)
      );
    });
  if (coverageRegression || splitOnlyRefresh) {
    if (!upgraded) return previous;
    return {
      ...upgraded,
      sourceT: imported
        ? minuteEnd
        : Math.max(previous.sourceT ?? previous.t, snapshot.sourceT ?? snapshot.t),
      ...(imported ? { serviceSnapshotOverlay: true } : {}),
    };
  }
  const replaced = replaceSameMinuteChainSnapshot(previous, snapshot);
  if (!imported) return replaced;
  return {
    ...replaced,
    byStrike: { ...previous.byStrike, ...snapshot.byStrike },
    hydrationFrom: Number.isFinite(previous.hydrationFrom)
      ? Math.min(previous.hydrationFrom, snapshot.hydrationFrom)
      : snapshot.hydrationFrom,
    hydrationTo: Number.isFinite(previous.hydrationTo)
      ? Math.max(previous.hydrationTo, snapshot.hydrationTo)
      : snapshot.hydrationTo,
    sourceT: minuteEnd,
    serviceSnapshotOverlay: true,
  };
}

export function mergeReplayChainSnaps(liveSnapshots, frames, { revalidate = false } = {}) {
  let snapshots = (liveSnapshots || []).map(stampImportedChainSnapshot);
  const framesByMinute = new Map();
  for (const frame of frames) {
    const minute = Math.floor(frame.t / BUFFER_MINUTE_MS);
    if (!framesByMinute.has(minute)) framesByMinute.set(minute, []);
    framesByMinute.get(minute).push(frame);
  }
  for (const [minute, minuteFrames] of framesByMinute) {
    minuteFrames.sort((left, right) => left.t - right.t);
    const existingIndex = snapshots.findIndex(
      (snapshot) => Math.floor(snapshot.t / BUFFER_MINUTE_MS) === minute
    );
    const existing = snapshots[existingIndex];
    const checkedFrames = existing && checkedFramesBySnapshot.get(existing);
    if (checkedFrames?.length === minuteFrames.length
      && checkedFrames.every((frame, index) => frame === minuteFrames[index])) continue;
    const existingIsLegacy = existing && isLegacyLegSplitSnapshot(existing);
    const existingIsCurrent = (sourceTime) => existing && !revalidate && !existingIsLegacy
      && !existing.serviceSnapshotOverlay && Number.isFinite(existing.sourceT)
      && existing.sourceT > sourceTime;
    const newestTime = minuteFrames[minuteFrames.length - 1].t;
    if (existingIsCurrent(newestTime)) continue;
    let snapshot = null;
    for (const frame of minuteFrames) {
      const built = buildChainSnapshot(frame.options.rowsBySym, frame.options.spot, frame.t);
      if (!built) continue;
      snapshot = snapshot
        ? mergeReplaySameMinuteSnapshot(snapshot, built)
        : built;
    }
    if (!snapshot) continue;
    if (existingIndex !== -1) {
      if (existingIsCurrent(snapshot.sourceT)) continue;
      const merged = mergeReplaySameMinuteSnapshot(
        existing, snapshot, { legacy: existingIsLegacy },
      );
      snapshots[existingIndex] = merged;
      checkedFramesBySnapshot.set(snapshots[existingIndex], minuteFrames.slice());
    } else {
      snapshots = mergeChainSnapshotSeries(snapshots, snapshot);
      checkedFramesBySnapshot.set(snapshots[snapshots.length - 1], minuteFrames.slice());
    }
  }
  return snapshots.sort((left, right) => left.t - right.t);
}

function mergeSameDayReplayChainSnaps(
  activeSnaps,
  refreshedFrames,
  tradingDate,
) {
  const sameDaySnaps = (activeSnaps || []).filter(
    (snapshot) => etDateKey(snapshot.t) === tradingDate,
  );
  return mergeReplayChainSnaps(sameDaySnaps, refreshedFrames, { revalidate: true });
}

function normalizeReplayFrame(frame) {
  return {
    ...frame,
    indexData: frame.indexData ? sanitizeIndexData(frame.indexData) : frame.indexData,
    aggregationState: frame.aggregationState
      ? sanitizeAggregationState(frame.aggregationState)
      : frame.aggregationState,
  };
}

function mergeMinuteFrames(storedFrames, liveFrames) {
  const liveMinutes = new Set(
    liveFrames.map((frame) => Math.floor(frame.t / BUFFER_MINUTE_MS)),
  );
  return [
    ...storedFrames.filter(
      (frame) => !liveMinutes.has(Math.floor(frame.t / BUFFER_MINUTE_MS)),
    ),
    ...liveFrames,
  ].sort((a, b) => a.t - b.t);
}

function cutoffIndex(frames, cutoffMs) {
  let index = 0;
  if (!Number.isFinite(cutoffMs)) return index;
  for (let i = 0; i < frames.length; i += 1) {
    if (frames[i].t <= cutoffMs) index = i;
    else break;
  }
  return index;
}

function preserveCutoffFrame(canonicalFrames, activeFrames, cutoffMs) {
  const cutoffFrame = Number.isFinite(cutoffMs)
    ? activeFrames.find((frame) => frame.t === cutoffMs)
    : null;
  const frames = cutoffFrame && !canonicalFrames.some((frame) => frame.t === cutoffMs)
    ? [...canonicalFrames, { ...cutoffFrame, preservedCutoff: true }]
      .sort((left, right) => left.t - right.t)
    : canonicalFrames;
  return { frames, index: cutoffIndex(frames, cutoffMs) };
}

function buildFixtureBlob({ version, recordedAt, cadenceMs, frames }) {
  const parts = [
    '{"version":', JSON.stringify(version),
    ',"recordedAt":', JSON.stringify(recordedAt),
    ',"cadenceMs":', JSON.stringify(cadenceMs),
    ',"frames":[',
  ];
  for (let i = 0; i < frames.length; i++) {
    if (i) parts.push(',');
    parts.push(JSON.stringify(frames[i]));
  }
  parts.push(']}');
  return new Blob(parts, { type: 'application/json' });
}

function trimmedFilename(filename, suffix) {
  const dot = filename.lastIndexOf('.');
  if (dot === -1) return `${filename}${suffix}`;
  return `${filename.slice(0, dot)}${suffix}${filename.slice(dot)}`;
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function makeCheckpointEnvelope({ sessionId, recordedAt, frames }) {
  return {
    sessionId,
    version: FIXTURE_VERSION,
    recordedAt,
    updatedAt: Date.now(),
    cadenceMs: REC_CADENCE_MS,
    frames,
  };
}

function summarizeCheckpoint(checkpoint) {
  const frames = Array.isArray(checkpoint?.frames) ? checkpoint.frames : [];
  return {
    sessionId: checkpoint?.sessionId ?? null,
    count: frames.length,
    recordedAt: checkpoint?.recordedAt ?? null,
    updatedAt: checkpoint?.updatedAt ?? null,
  };
}

function itemTimestampMs(item) {
  const value = item?.timestamp ?? item?.time ?? item?.t
    ?? item?.created_at ?? item?.createdAt ?? item?.inserted_at ?? item?.insertedAt
    ?? item?.updated_at ?? item?.updatedAt;
  const numeric = Number(value);
  const parsed = typeof value === 'string' && !Number.isFinite(numeric)
    ? Date.parse(value)
    : numeric;
  if (!Number.isFinite(parsed)) return null;
  return parsed < 10_000_000_000 ? parsed * 1000 : parsed;
}

// NOTE: an untimestamped item is KEPT, i.e. the cutoff fails OPEN. Every series
// audited for Phase 2 carries a timestamp, so this is latent today, but a field
// rename upstream would silently leak post-cutoff data into a replayed view
// rather than dropping it -- which is the failure direction that lets a user
// mistake replayed state for something it is not. If a series without reliable
// timestamps is ever added here, invert this to drop unknowns instead.
function filterSeries(series, cutoffMs) {
  if (Array.isArray(series)) {
    return series.filter((item) => {
      const timestamp = itemTimestampMs(item);
      return timestamp == null || timestamp <= cutoffMs;
    });
  }
  if (!series || typeof series !== 'object') return series;
  return Object.fromEntries(
    Object.entries(series).map(([key, value]) => [key, filterSeries(value, cutoffMs)])
  );
}

function filterIndexData(indexData, cutoffMs) {
  return sanitizeIndexData(Object.fromEntries(
    Object.entries(indexData || {}).map(([symbol, bars]) => [
      symbol,
      Array.isArray(bars)
        ? bars.filter((bar) => {
            const timestamp = itemTimestampMs(bar);
            return timestamp == null || timestamp <= cutoffMs;
          })
        : bars,
    ])
  ));
}

function buildFixtureChainSnaps(frames) {
  useOptionsStore.setState({
    chainSnaps: [],
    flowCalculationSnaps: null,
    chainSnapsFallback: false,
  });
  for (const frame of frames) {
    useOptionsStore.setState({
      rowsBySym: frame.options.rowsBySym,
      spot: frame.options.spot,
      hasLiveOptionsData: true,
    });
    useOptionsStore.getState().captureChainSnapshotAt(frame.t);
  }
  return useOptionsStore.getState().chainSnaps;
}

// ONE deep clone serves both views. replaySource is a read-only projection over
// the same cloned objects as liveStoreState -- applyCutoff only ever derives new
// filtered arrays from it and never mutates in place, so they can share.
// Previously these were two independent JSON.parse(JSON.stringify(...)) passes
// over the same up-to-400-entry chainSnaps and all four Greeks series, both
// resident for the whole replay; on a full day that was the most expensive thing
// toggle-enable did, against a "visually instant" acceptance criterion.
function captureLiveSnapshot() {
  const options = useOptionsStore.getState();
  const greeks = useGreeksWebsocketDataStore.getState();
  const websocket = useWebSocketDataStore.getState();
  const mi = useMarketIntelligenceStore.getState();
  const cloned = JSON.parse(JSON.stringify({
    options: {
      rowsBySym: options.rowsBySym,
      rowsTradingDate: options.rowsTradingDate,
      spot: options.spot,
      chainSnaps: options.chainSnaps,
      flowCalculationSnaps: null,
      chainSnapsFallback: options.chainSnapsFallback,
      chainSnapshotHydration: options.chainSnapshotHydration,
      status: options.status,
      isDataReady: options.isDataReady,
      hasLiveOptionsData: options.hasLiveOptionsData,
    },
    greeks: {
      greekExposures: greeks.greekExposures,
      dynamicLevels: greeks.dynamicLevels,
      gexTrendStrength: greeks.gexTrendStrength,
      topOptions: greeks.topOptions,
      adaptiveTopOptions: greeks.adaptiveTopOptions,
      adaptiveDynamicLevels: greeks.adaptiveDynamicLevels,
      lastValidData: greeks.lastValidData,
    },
    indexData: pickIndexData(websocket.indexData),
    mi: {
      visibleFrontendPosts: mi.visibleFrontendPosts,
      recentFrontendPosts: mi.recentFrontendPosts,
      tradingDate: mi.tradingDate,
      hasHydratedPosts: mi.hasHydratedPosts,
      updateSource: mi.updateSource,
      updateReason: mi.updateReason,
    },
  }));

  return {
    liveStoreState: { options: cloned.options, greeks: cloned.greeks, mi: cloned.mi },
    replaySource: {
      chainSnaps: cloned.options.chainSnaps,
      greeks: {
        greekExposures: cloned.greeks.greekExposures,
        gexTrendStrength: cloned.greeks.gexTrendStrength,
        adaptiveTopOptions: cloned.greeks.adaptiveTopOptions,
        adaptiveDynamicLevels: cloned.greeks.adaptiveDynamicLevels,
      },
      indexData: cloned.indexData,
      miPosts: cloned.mi.recentFrontendPosts,
    },
  };
}

// A current-day MI dataset is a snapshot at query time, so a session cached in
// the morning still reports `complete` with only the early cards. Replay merges
// what the live feed rendered at entry. While playing, applyCutoff owns the
// store's recent/visible fields, so those come from the entry capture; it never
// writes the historical fields, and live snapshots that arrive meanwhile wait in
// pendingMiSnapshots. The cutoff hides queued posts until the post-close range
// extension reaches them. Later entries win the id merge, so the freshest copy
// of a post replaces an older one.
function liveReplayMiPosts(playback, tradingDate) {
  const mi = useMarketIntelligenceStore.getState();
  const playing = playback.mode === 'playing';
  const live = playing ? (playback.liveStoreState?.mi || {}) : mi;
  const queued = playing
    ? pendingMiSnapshots.flatMap(({ payload }) => [
      ...(payload?.visible_frontend_posts || payload?.visibleFrontendPosts || []),
      ...(payload?.recent_frontend_posts || payload?.recentFrontendPosts || []),
    ])
    : [];
  return [
    ...(mi.historicalTradingDate === tradingDate ? (mi.historicalFrontendPosts || []) : []),
    ...(live.visibleFrontendPosts || []),
    ...(live.recentFrontendPosts || []),
    ...queued,
  ];
}

function restoredLiveTradingDate() {
  const websocket = useWebSocketDataStore.getState();
  const options = useOptionsStore.getState();
  const times = [
    ...(websocket.indexData?.SPX || []).map((bar) => Number(bar?.time)),
    ...(websocket.indexData?.ES || []).map((bar) => Number(bar?.time)),
    ...(options.chainSnaps || []).map((snapshot) => Number(snapshot?.t)),
  ].filter(Number.isFinite);
  return times.length ? etDateKey(Math.max(...times)) : mostRecentSessionDate();
}

function emptyPendingLiveUpdates() {
  return {
    optionInitRows: null,
    optionStatus: null,
    aggSnapshot: null,
    chainSnaps: [],
  };
}

function mergeAggPatch(existing, incoming) {
  const merged = {
    ...existing,
    ...incoming,
    sums: { ...(existing?.sums || {}) },
    bands: { ...(existing?.bands || {}) },
  };
  for (const [field, value] of Object.entries(incoming?.sums || {})) {
    const numeric = Number(value);
    if (Number.isFinite(numeric)) {
      merged.sums[field] = (Number(merged.sums[field]) || 0) + numeric;
    }
  }
  for (const [band, fields] of Object.entries(incoming?.bands || {})) {
    merged.bands[band] = { ...(merged.bands[band] || {}) };
    for (const [field, value] of Object.entries(fields || {})) {
      const numeric = Number(value);
      if (Number.isFinite(numeric)) {
        merged.bands[band][field] = (Number(merged.bands[band][field]) || 0) + numeric;
      }
    }
  }
  return merged;
}

// The freshest index bars available to a replay projection. `replaySource` is
// cloned once at replay entry, so without this a reconnect's historical fetch --
// which lands in the live store while replay is running -- never reaches the
// chart, and it stops at whatever minute the snapshot happened to be taken.
//
// Scoped to the session being replayed. A replay of a PREVIOUS day runs beside a
// live feed publishing today's bars, and splicing those in would draw today's
// prices on yesterday's timeline. When the live store holds nothing for the
// replayed day the existing projection stands: bars that were never captured
// cannot be recovered from a feed that only serves the current session.
function liveReplayIndexData(fallback, replayDateKey) {
  const websocket = useWebSocketDataStore.getState();
  const candidate = websocket._replayBufferedIndexData
    ?? websocket._liveIndexDataSnapshot
    ?? null;
  if (!candidate) return fallback ?? {};
  if (!replayDateKey) return candidate;

  const scoped = {};
  let hasReplayDayBars = false;
  for (const [symbol, bars] of Object.entries(candidate)) {
    const sameDay = (Array.isArray(bars) ? bars : []).filter((bar) => (
      Number.isFinite(bar?.time) && etDateKey(bar.time) === replayDateKey
    ));
    if (sameDay.length) hasReplayDayBars = true;
    scoped[symbol] = sameDay;
  }
  return hasReplayDayBars ? mergeIndexData(fallback, scoped) : (fallback ?? {});
}

function replayDateKeyOf(frames, index) {
  const t = frames?.[index]?.t ?? frames?.[0]?.t;
  return Number.isFinite(t) ? etDateKey(t) : null;
}

function buildShadowOptions(liveOptions, pending) {
  const today = etDateKey(Date.now());
  // Mirrors applyInit/applyRow: rows the live store carried over from an earlier
  // session are dropped, not merged, when this session's first payload lands.
  // The shadow map is the live store's future, so it obeys the same rule.
  const carriedDate = liveOptions?.rowsTradingDate ?? null;
  const staleRows = Boolean(carriedDate && carriedDate !== today);
  let rowsBySym = (pending.optionInitRows || staleRows)
    ? {}
    : { ...(liveOptions?.rowsBySym || {}) };
  // Spot rolls over with the rows, same as applyRow: a carried spot against a
  // cleared chain is the previous session leaking through.
  let spot = staleRows ? null : (liveOptions?.spot ?? null);
  let rowsTradingDate = staleRows ? null : carriedDate;
  if (pending.optionInitRows) {
    for (const row of pending.optionInitRows) {
      if (!row?.sym) continue;
      rowsBySym[row.sym] = row;
      if (Number.isFinite(row.S)) spot = row.S;
      rowsTradingDate = today;
    }
  }
  for (const row of pendingOptionRows.values()) {
    rowsBySym[row.sym] = row;
    if (Number.isFinite(row.S)) spot = row.S;
    rowsTradingDate = today;
  }
  if (pendingLiveSpot != null) spot = pendingLiveSpot;
  return {
    rowsBySym,
    spot,
    rowsTradingDate,
    hasLiveOptionsData: Object.keys(rowsBySym).length > 0,
  };
}

function buildShadowGreeks() {
  const greeks = pendingGreeksScratch.getState();
  return { dynamicLevels: greeks.dynamicLevels, topOptions: greeks.topOptions };
}

function drainPendingLiveUpdates(
  liveStoreState,
  pending,
  optionRows,
  aggPatches,
  liveSpot,
  liveGreeks,
  miSnapshots,
) {
  if (liveStoreState) {
    useOptionsStore.setState(liveStoreState.options);
    useGreeksWebsocketDataStore.setState(liveStoreState.greeks);
    if (liveStoreState.mi) useMarketIntelligenceStore.setState(liveStoreState.mi);
  }
  for (const { payload, meta } of miSnapshots || []) {
    useMarketIntelligenceStore.getState().setSnapshot(payload, meta);
  }

  const options = useOptionsStore.getState();
  if (pending.optionInitRows) options.applyInit(pending.optionInitRows);
  for (const row of optionRows) options.applyRow(row);
  if (pending.aggSnapshot) {
    options.applyAggSnapshot(pending.aggSnapshot.payload, pending.aggSnapshot.keepBars);
  }
  for (const patch of aggPatches) options.applyAggPatch(patch.payload, patch.keepBars);
  if (liveSpot != null) useOptionsStore.setState({ spot: liveSpot });
  if (pending.optionStatus) options.setStatus(pending.optionStatus);
  if (pending.chainSnaps.length) {
    useOptionsStore.setState((state) => ({
      chainSnaps: pending.chainSnaps.reduce(
        (snaps, snapshot) => mergeChainSnapshotSeries(snaps, snapshot),
        state.chainSnapsFallback ? [] : state.chainSnaps,
      ),
      flowCalculationSnaps: null,
      chainSnapsFallback: false,
    }));
  }

  if (liveGreeks) useGreeksWebsocketDataStore.setState(liveGreeks);
}

function applyCutoff(frame, replaySource, previousCutoffMs = null) {
  if (!frame) return;
  const cutoffMs = frame.t;
  const hasOptionsData = frame.datasetAnchor !== true
    && Object.keys(frame.options.rowsBySym || {}).length > 0
    && Number.isFinite(frame.options.spot);
  const projection = replaySource?.datasets
    ? projectReplayDatasets(replaySource.datasets, cutoffMs)
    : null;
  const frameGreeks = frame.greeks || {};
  const sourceGreeks = replaySource?.greeks || {};
  const projectedGreeks = projection?.greeks;
  const canProjectGreeks = (...datasets) => Boolean(projection) && datasets.every(
    (dataset) => replayDatasetCanProject(replaySource?.coverage, dataset, cutoffMs),
  );

  const sourceChainSnaps = replaySource?.chainSnaps || [];
  const chainSnapshotEligibleAt = (snapshot) => snapshot.sourceT ?? chainSnapshotMinuteEnd(snapshot.t);
  let visibleChainSnaps = sourceChainSnaps.filter(
    (snapshot) => snapshot.t <= cutoffMs && chainSnapshotEligibleAt(snapshot) <= cutoffMs,
  );
  const cutoffMinuteMs = Math.floor(cutoffMs / BUFFER_MINUTE_MS) * BUFFER_MINUTE_MS;
  const closeMinute = sessionCloseEpochMs(cutoffMs);
  const atLastSessionMinute = cutoffMinuteMs === closeMinute - BUFFER_MINUTE_MS;
  // Historical service overlays complete the closing flow; later live captures
  // remain behind their source-time gate.
  let flowCalculationSnaps = sourceChainSnaps.filter((snapshot) => {
    if (snapshot.t <= cutoffMs) return chainSnapshotEligibleAt(snapshot) <= cutoffMs
      || (atLastSessionMinute && snapshot.serviceSnapshotOverlay
        && snapshot.t === cutoffMinuteMs);
    return atLastSessionMinute
      && Math.floor(snapshot.t / BUFFER_MINUTE_MS) * BUFFER_MINUTE_MS === closeMinute;
  });
  const cutoffMinute = Math.floor(cutoffMs / BUFFER_MINUTE_MS);
  const hasIneligibleCutoffMinute = sourceChainSnaps.some((snapshot) => (
    Math.floor(snapshot.t / BUFFER_MINUTE_MS) === cutoffMinute
    && chainSnapshotEligibleAt(snapshot) > cutoffMs
  ));
  if (hasIneligibleCutoffMinute) {
    const fallback = buildChainSnapshot(frame.options.rowsBySym, frame.options.spot, frame.t);
    visibleChainSnaps = mergeChainSnapshotSeries(visibleChainSnaps, fallback);
    if (!flowCalculationSnaps.some((snapshot) => snapshot.t === cutoffMinuteMs)) {
      flowCalculationSnaps = mergeChainSnapshotSeries(flowCalculationSnaps, fallback)
        .sort((left, right) => left.t - right.t);
    }
  }

  useOptionsStore.getState().applyReplayChainSnapshotProjection({
    rowsBySym: frame.options.rowsBySym,
    tradingDate: replaySource?.tradingDate || etDateKey(frame.t),
    spot: frame.options.spot,
    snaps: visibleChainSnaps,
    flowCalculationSnaps,
    hasOptionsData,
  });
  useGreeksWebsocketDataStore.setState({
    greekExposures: canProjectGreeks('greeks:exposures')
      ? projectedGreeks.greekExposures
      : filterSeries(frameGreeks.greekExposures ?? sourceGreeks.greekExposures ?? {}, cutoffMs),
    dynamicLevels: canProjectGreeks('greeks:dynamicLevels')
      ? projectedGreeks.dynamicLevels
      : frameGreeks.dynamicLevels ?? {},
    gexTrendStrength: canProjectGreeks('greeks:gexTrend')
      ? projectedGreeks.gexTrendStrength
      : filterSeries(frameGreeks.gexTrendStrength ?? sourceGreeks.gexTrendStrength ?? {}, cutoffMs),
    topOptions: canProjectGreeks('greeks:rankedOptions')
      ? projectedGreeks.topOptions
      : frameGreeks.topOptions ?? {},
    adaptiveTopOptions: canProjectGreeks('greeks:rankedOptions')
      ? projectedGreeks.adaptiveTopOptions
      : filterSeries(frameGreeks.adaptiveTopOptions ?? sourceGreeks.adaptiveTopOptions ?? {}, cutoffMs),
    adaptiveDynamicLevels: canProjectGreeks('greeks:dynamicLevels')
      ? projectedGreeks.adaptiveDynamicLevels
      : filterSeries(
          frameGreeks.adaptiveDynamicLevels ?? sourceGreeks.adaptiveDynamicLevels ?? {},
          cutoffMs,
        ),
  });
  const nextIndexData = projection?.indexData
    ? filterIndexData(mergeIndexData(
        projection.indexData,
        frame.indexData ?? replaySource?.indexData ?? {},
      ), cutoffMs)
    : filterIndexData(frame.indexData ?? replaySource?.indexData ?? {}, cutoffMs);
  const nextAggregationState = frame.aggregationState
    ? sanitizeAggregationState(frame.aggregationState)
    : {};
  useWebSocketDataStore.setState((state) => {
    // Source hydration can add bars while the replay cursor stays fixed. The
    // chart must refetch only when that fixed-cutoff projection changes.
    const sourceChangedAtSameCutoff = Number.isFinite(previousCutoffMs)
      && previousCutoffMs === cutoffMs
      && !indexDataMatches(state.indexData, nextIndexData);
    return {
      indexData: nextIndexData,
      aggregationState: nextAggregationState,
      ...(sourceChangedAtSameCutoff ? {
        indexHistoryVersion: state.indexHistoryVersion + 1,
      } : {}),
    };
  });
  if (projection || Array.isArray(replaySource?.miPosts)) {
    const miPosts = replayMiPostsAtCutoff(replaySource, cutoffMs);
    const tradingDate = replaySource.tradingDate || null;
    const hasHydratedPosts = Boolean(projection && replaySource.coverage?.mi?.status === 'complete');
    const miState = useMarketIntelligenceStore.getState();
    if (miState.visibleFrontendPosts !== miPosts
      || miState.recentFrontendPosts !== miPosts
      || miState.tradingDate !== tradingDate
      || miState.hasHydratedPosts !== hasHydratedPosts
      || miState.updateSource !== 'replay_v2'
      || miState.updateReason !== null) {
      useMarketIntelligenceStore.setState({
        visibleFrontendPosts: miPosts,
        recentFrontendPosts: miPosts,
        tradingDate,
        hasHydratedPosts,
        updateSource: 'replay_v2',
        updateReason: null,
      });
    }
  }
}

function captureAutoBufferFrame(set, get) {
  if (!isDuringMarketHours(Date.now())) return false;

  const today = etDateKey(Date.now());
  const playback = get();
  let frame;
  let replayChainSnapshot = null;
  if (playback.mode === 'playing' && playback.liveStoreState) {
    const shadowOptions = buildShadowOptions(
      playback.liveStoreState.options,
      playback.pendingLiveUpdates,
    );
    if (!shadowOptions.hasLiveOptionsData || shadowOptions.rowsTradingDate !== today) {
      return false;
    }
    const shadowGreeks = buildShadowGreeks();
    const websocket = useWebSocketDataStore.getState();
    frame = captureBufferFrame({
      options: shadowOptions,
      greeks: shadowGreeks,
      aggregationState:
        websocket._replayBufferedAggregationState
        ?? websocket._liveAggregationSnapshot
        ?? {},
    });
    const built = buildChainSnapshot(
      shadowOptions.rowsBySym,
      shadowOptions.spot,
      frame.t,
    );
    replayChainSnapshot = built;
  } else {
    const options = useOptionsStore.getState();
    if (!options.hasLiveOptionsData || options.rowsTradingDate !== today) return false;
    frame = captureBufferFrame();
  }

  const nowKey = etDateKey(frame.t);
  const minuteKey = Math.floor(frame.t / BUFFER_MINUTE_MS);
  set((state) => {
    const existing = state.bufferDate === nowKey ? state.bufferFrames : [];
    const last = existing[existing.length - 1];
    const sameMinute = last && Math.floor(last.t / BUFFER_MINUTE_MS) === minuteKey;
    return {
      bufferFrames: sameMinute
        ? [...existing.slice(0, -1), frame]
        : [...existing, frame],
      bufferDate: nowKey,
      ...(replayChainSnapshot ? {
        pendingLiveUpdates: {
          ...state.pendingLiveUpdates,
          chainSnaps: mergeChainSnapshotSeries(
            state.pendingLiveUpdates.chainSnaps,
            replayChainSnapshot,
          ),
        },
      } : {}),
    };
  });
  if (replayChainSnapshot) get().refreshReplaySources();
  void persistReplaySnapshot(nowKey, frame).catch((err) => {
    const message = err instanceof Error ? err.message : String(err);
    set({ historyError: message || 'Replay history save failed.' });
  });
  return true;
}

const usePlaybackStore = create((set, get) => ({
  mode: 'off',
  frames: [],
  index: -1,
  playing: false,
  speed: 1,
  saving: false,
  saveError: null,
  lastSavedFilename: null,
  lastTrimmedFilename: null,
  checkpointing: false,
  checkpointError: null,
  recoveredFromCheckpoint: false,
  recoveryAvailable: false,
  recoverySummary: null,
  _recordingStartedAt: null,
  _recordingSessionId: null,
  _checkpointSeq: 0,
  _recId: null,
  _playId: null,
  replayCutoffMs: null,
  replaySource: null,
  replayCoverage: null,
  replayDatasetSession: null,
  replayDatasetCacheCandidate: null,
  stagedReplayCoverage: null,
  stagedReplayDatasetSession: null,
  stagedReplayDatasetCacheCandidate: null,
  stagedReplayFrames: [],
  stagedReplayDate: null,
  replayDateSelection: null,
  replayGap: null,
  replayHistoryActive: false,
  partialWidgetIds: [],
  liveStoreState: null,
  historyHydrated: false,
  historyHydrating: false,
  historyHydratingRequestKey: null,
  historySourcesHydrating: false,
  historySourcesHydratingDate: null,
  historyIntentChecking: false,
  historyExitPersisting: false,
  _historyGeneration: 0,
  _historyExitPersistenceGeneration: 0,
  historyError: null,
  // The app owns one playback store. If that changes, these module-level queues
  // must become instance-scoped. Maps keep hot-path writes O(1) without notifying
  // Zustand subscribers; Greeks retain reducer order in the scratch store.
  pendingLiveUpdates: emptyPendingLiveUpdates(),

  // Test affordances only; production callers should use the queue lifecycle.
  resetPendingLiveQueues: () => clearPendingLiveQueues(),
  getPendingLiveQueueSnapshot: () => ({
    optionRows: [...pendingOptionRows.values()],
    aggPatches: [...pendingAggPatches.values()],
    greeks: getGreeksDataState(pendingGreeksScratch),
  }),

  queueLiveOptionInit: (rows) => {
    pendingOptionRows.clear();
    for (const row of rows || []) {
      if (Number.isFinite(row?.S)) pendingLiveSpot = row.S;
    }
    set((state) => ({
      pendingLiveUpdates: { ...state.pendingLiveUpdates, optionInitRows: rows },
    }));
  },

  queueLiveOptionRow: (row) => {
    if (!row?.sym) return;
    pendingOptionRows.delete(row.sym);
    pendingOptionRows.set(row.sym, row);
    if (Number.isFinite(row.S)) pendingLiveSpot = row.S;
  },

  queueLiveOptionStatus: (status) => set((state) => ({
    pendingLiveUpdates: { ...state.pendingLiveUpdates, optionStatus: status },
  })),

  queueLiveAggSnapshot: (payload, keepBars) => {
    pendingAggPatches.clear();
    if (Number.isFinite(payload?.spot)) pendingLiveSpot = payload.spot;
    set((state) => ({
      pendingLiveUpdates: {
        ...state.pendingLiveUpdates,
        aggSnapshot: { payload, keepBars },
      },
    }));
  },

  queueLiveAggPatch: (payload, keepBars) => {
    if (!payload?.bucket) return;
    const existing = pendingAggPatches.get(payload.bucket);
    pendingAggPatches.delete(payload.bucket);
    pendingAggPatches.set(payload.bucket, {
      payload: mergeAggPatch(existing?.payload, payload),
      keepBars,
    });
  },

  queueLiveGreekInitial: (data) => {
    pendingGreeksScratch?.getState().setInitialData('spx', data);
    const state = get();
    if (state.replayHistoryActive && state.replayCutoffMs != null && pendingGreeksScratch) {
      const scratch = pendingGreeksScratch.getState();
      const replaySource = {
        ...state.replaySource,
        greeks: {
          ...(state.replaySource?.greeks || {}),
          greekExposures: scratch.greekExposures,
          gexTrendStrength: scratch.gexTrendStrength,
          adaptiveTopOptions: scratch.adaptiveTopOptions,
          adaptiveDynamicLevels: scratch.adaptiveDynamicLevels,
        },
      };
      set({ replaySource });
      applyCutoff(state.frames[state.index], replaySource, state.replayCutoffMs);
    }
  },

  queueLiveGreekOp: (type, payload) => {
    applyGreeksOp(pendingGreeksScratch, type, payload);
  },

  queueLiveMiSnapshot: (payload, meta = {}) => {
    pendingMiSnapshots.push({ payload, meta });
  },

  refreshReplaySources: () => {
    const state = get();
    if (!state.replayHistoryActive || state.index < 0) return;
    const chainSnaps = state.pendingLiveUpdates.chainSnaps.reduce(
      (snapshots, snapshot) => mergeChainSnapshotSeries(snapshots, snapshot),
      state.replaySource?.chainSnaps || [],
    );
    const replaySource = {
      ...state.replaySource,
      chainSnaps,
      indexData: liveReplayIndexData(
        state.replaySource?.indexData,
        replayDateKeyOf(state.frames, state.index),
      ),
    };
    set({ replaySource });
    applyCutoff(state.frames[state.index], replaySource, state.replayCutoffMs);
  },

  refreshReplayMiPosts: () => {
    const state = get();
    const tradingDate = state.replaySource?.tradingDate;
    if (state.mode !== 'playing' || !state.replayHistoryActive || !tradingDate || state.index < 0) {
      return false;
    }
    const replaySource = {
      ...state.replaySource,
      miPosts: liveReplayMiPosts(state, tradingDate),
    };
    set({ replaySource });
    applyCutoff(state.frames[state.index], replaySource, state.replayCutoffMs);
    return true;
  },

  // Silent intraday auto-buffer (TD043): captures frames in the background,
  // independent of manual record/replay, so a simplified review surface
  // always has "the latest trade day" without a Record click. Production
  // replay keeps it running against the pending live-update shadow; Recorder
  // fixture playback still pauses it because that path has no live-day review
  // contract.
  //
  // Now a production-visible mode (TD043 Phase 1 follow-up review). Captures
  // on their own BUFFER_MINUTE_MS cadence, self-rescheduled to land ~500ms
  // after each ET minute boundary rather than sharing the recorder's 15s
  // REC_CADENCE_MS -- a fixed-period tick that only *retained* one sample per
  // minute still ran the ~287KB JSON.stringify/parse deep clone in
  // captureFrame 4x every minute; this actually calls captureFrame once.
  // Retained frames contain only per-minute snapshot fields; cumulative
  // series are captured once when replay starts and filtered by cutoff. The same-minute
  // replace-not-append check stays as a defensive safety net for
  // pause/resume landing in the same minute (e.g. a fixture load immediately
  // followed by exitPlayback), not as the primary capture-rate control.
  // The retained day rolls only on the first successful open-market capture;
  // until then, the latest completed trade day remains replayable.
  bufferFrames: [],
  bufferDate: null,
  _bufferId: null,

  restoreReplayIfActive: async ({ partialWidgetIds = [], tradingDate = null, discovery = null } = {}) => {
    const initial = get();
    if (
      initial.historyIntentChecking
      || initial.historyHydrated
      || initial.historyExitPersisting
    ) return false;
    const historyGeneration = initial._historyGeneration;
    set({ historyIntentChecking: true });
    try {
      const replayState = await loadPersistedReplayState();
      if (
        get()._historyGeneration !== historyGeneration
        || get().historyExitPersisting
      ) return false;
      const expectedDay = tradingDate || etDateKey(Date.now());
      if (!replayState?.active || replayState.day !== expectedDay) {
        set({ historyIntentChecking: false });
        return false;
      }
      await get().hydrateReplayHistory({
        partialWidgetIds,
        restore: true,
        tradingDate,
        discovery,
        loadDatasetRecords: true,
      });
      set({ historyIntentChecking: false });
      return get().mode === 'playing';
    } catch (err) {
      if (get()._historyGeneration !== historyGeneration) return false;
      const message = err instanceof Error ? err.message : String(err);
      set({
        historyIntentChecking: false,
        historyError: message || 'Replay history check failed.',
      });
      return false;
    }
  },

  hydrateReplayHistory: async ({
    partialWidgetIds = [], restore = false, tradingDate = null, discovery = null,
    loadDatasetRecords = false,
  } = {}) => {
    const initial = get();
    const hydrationRequestKey = replayHistoryHydrationRequestKey(
      tradingDate,
      discovery,
      loadDatasetRecords,
    );
    if (initial.historyHydrated && (!tradingDate || initial.bufferDate === tradingDate)) {
      if (discovery) {
        void get().hydrateReplayDatasetSources({ tradingDate: initial.bufferDate, discovery });
      }
      return initial.bufferFrames.length;
    }
    if (get().historyHydrating) {
      const inFlightRequestKey = initial.historyHydratingRequestKey;
      await new Promise((resolve) => {
        if (!get().historyHydrating) {
          resolve();
          return;
        }
        const unsub = usePlaybackStore.subscribe((state) => {
          if (!state.historyHydrating) {
            unsub();
            resolve();
          }
        });
      });
      if (inFlightRequestKey !== hydrationRequestKey) {
        return get().hydrateReplayHistory({
          partialWidgetIds,
          restore,
          tradingDate,
          discovery,
          loadDatasetRecords,
        });
      }
      return get().bufferFrames.length;
    }
    set({
      historyHydrating: true,
      historyHydratingRequestKey: hydrationRequestKey,
      ...(loadDatasetRecords ? {
        historySourcesHydrating: true,
        historySourcesHydratingDate: tradingDate || initial.bufferDate,
      } : {}),
    });
    const historyGeneration = get()._historyGeneration;
    const today = etDateKey(Date.now());
    try {
      const { day: historyDay, snapshots, replayState } = await loadReplayHistory(today, tradingDate);
      const loadedDatasetSession = await loadReplayDatasetSession(
        historyDay,
        { includeRecords: loadDatasetRecords },
      ).catch(() => null);
      const metadataMatchesDiscovery = !discovery
        || replayDatasetSessionMetadataMatchesDiscovery(loadedDatasetSession, discovery);
      const datasetSession = loadDatasetRecords && (
        discovery
          ? replayDatasetSessionMatchesCoverage(loadedDatasetSession, discovery)
          : replayDatasetSessionIsStructurallyValid(loadedDatasetSession, historyDay)
      ) ? loadedDatasetSession : null;
      if (get()._historyGeneration !== historyGeneration) {
        return get().bufferFrames.length;
      }
      const legacyFrames = metadataMatchesDiscovery
        ? filterReplaySessionFrames(snapshots.map(historySnapshotToFrame).filter(isValidFrame))
        : [];
      const datasetFrames = filterReplaySessionFrames(
        replayDatasetFrames(datasetSession),
      );
      const storedFrames = mergeRecoveredReplayFrames(legacyFrames, datasetFrames);
      let hydratedDay = historyDay;
      let hydratedFrames = storedFrames;
      let hydratedDatasetSession = datasetSession;
      set((state) => {
        if (state._historyGeneration !== historyGeneration) return {};
        const staging = state.replayDateSelection?.tradingDate === tradingDate;
        const currentDayBufferIsAhead = !tradingDate
          && state.bufferDate === today
          && historyDay !== today
          && state.bufferFrames.length > 0;
        hydratedDay = currentDayBufferIsAhead ? today : historyDay;
        hydratedDatasetSession = currentDayBufferIsAhead ? null : datasetSession;
        const existingFrames = staging
          ? (state.stagedReplayDate === hydratedDay ? state.stagedReplayFrames : [])
          : (state.bufferDate === hydratedDay ? state.bufferFrames : []);
        hydratedFrames = mergeMinuteFrames(
          currentDayBufferIsAhead ? [] : storedFrames,
          existingFrames,
        );
        const hydratedCoverage = hydratedDatasetSession
          ? supplementReplayCoverageWithLocalDatasets(
            hydratedDatasetSession.coverage,
            replayCoverageDatasets(hydratedDatasetSession, hydratedFrames),
          )
          : null;
        return {
          ...(staging
            ? {
              stagedReplayFrames: hydratedFrames,
              stagedReplayDate: hydratedDay,
              stagedReplayDatasetSession: hydratedDatasetSession,
              stagedReplayCoverage: hydratedCoverage,
              ...(loadDatasetRecords
                ? { stagedReplayDatasetCacheCandidate: loadedDatasetSession }
                : {}),
            }
            : {
              bufferFrames: hydratedFrames,
              bufferDate: hydratedDay,
              replayDatasetSession: hydratedDatasetSession,
              replayCoverage: hydratedCoverage,
              ...(loadDatasetRecords
                ? { replayDatasetCacheCandidate: loadedDatasetSession }
                : {}),
            }),
          historyHydrated: true,
          historyHydrating: false,
          historyHydratingRequestKey: null,
          ...(loadDatasetRecords ? {
            historySourcesHydrating: false,
            historySourcesHydratingDate: null,
          } : {}),
          historyError: null,
        };
      });

      if (
        restore
        && get()._historyGeneration === historyGeneration
        && replayState?.active
        && replayState.day === hydratedDay
        && hydratedFrames.length
        && get().mode !== 'playing'
      ) {
        const { liveStoreState, replaySource: capturedSource } = captureLiveSnapshot();
        const sourceFallback = hydratedDatasetSession ? {
          greeks: {},
          indexData: {},
        } : {
          greeks: capturedSource.greeks || {},
          indexData: capturedSource.indexData || {},
        };
        if (get().loadFixture({
          frames: hydratedFrames,
          replaySource: {
            ...(hydratedDatasetSession || {}),
            chainSnaps: mergeReplayChainSnaps(
              (capturedSource.chainSnaps || []).filter(
                (snapshot) => etDateKey(snapshot.t) === hydratedDay,
              ),
              hydratedFrames,
            ),
            ...sourceFallback,
            miPosts: liveReplayMiPosts(
              get(),
              hydratedDatasetSession?.tradingDate || hydratedDay,
            ),
            tradingDate: hydratedDatasetSession?.tradingDate || hydratedDay,
            datasets: hydratedDatasetSession?.datasets,
            supplementalDatasets: hydratedDatasetSession?.supplementalDatasets,
            coverage: hydratedDatasetSession
              ? supplementReplayCoverageWithLocalDatasets(
                hydratedDatasetSession.coverage,
                replayCoverageDatasets(hydratedDatasetSession, hydratedFrames),
              )
              : null,
            session: hydratedDatasetSession?.session,
          },
          liveStoreState,
          partialWidgetIds,
          keepAutoBuffer: true,
          replayHistoryActive: true,
        })) {
          set({
            bufferFrames: hydratedFrames,
            bufferDate: hydratedDay,
          });
          get().seek(cutoffIndex(get().frames, Number(replayState.cutoffMs)));
        }
      }
      if (!loadDatasetRecords && loadedDatasetSession) {
        void get().hydrateReplayDatasetSources({ tradingDate: hydratedDay, discovery });
      }
      return hydratedFrames.length;
    } catch (err) {
      if (get()._historyGeneration !== historyGeneration) {
        return get().bufferFrames.length;
      }
      const message = err instanceof Error ? err.message : String(err);
      set({
        historyHydrated: true,
        historyHydrating: false,
        historyHydratingRequestKey: null,
        historySourcesHydrating: false,
        historySourcesHydratingDate: null,
        historyError: message || 'Replay history load failed.',
      });
      return 0;
    }
  },

  hydrateReplayDatasetSources: async ({ tradingDate = null, discovery = null } = {}) => {
    let initial = get();
    const historyDay = tradingDate || initial.bufferDate;
    if (!historyDay) return null;
    if (initial.historySourcesHydrating) {
      const inFlightDate = initial.historySourcesHydratingDate;
      await new Promise((resolve) => {
        const unsub = usePlaybackStore.subscribe((state) => {
          if (!state.historySourcesHydrating) {
            unsub();
            resolve();
          }
        });
      });
      const current = get();
      const completed = current.replayDateSelection?.tradingDate === historyDay
        ? current.stagedReplayDatasetSession
        : current.replayDatasetSession;
      if (inFlightDate === historyDay || completed?.tradingDate === historyDay) return completed;
      initial = current;
    }
    set({ historySourcesHydrating: true, historySourcesHydratingDate: historyDay });
    const historyGeneration = initial._historyGeneration;
    const loaded = await loadReplayDatasetSession(historyDay).catch(() => null);
    const datasetSession = discovery
      ? (replayDatasetSessionMatchesCoverage(loaded, discovery) ? loaded : null)
      : (replayDatasetSessionIsStructurallyValid(loaded, historyDay) ? loaded : null);
    if (get()._historyGeneration !== historyGeneration) return null;
    if (!datasetSession) {
      const current = get();
      const staging = current.replayDateSelection?.tradingDate === historyDay;
      const targetMatches = staging
        ? current.stagedReplayDate === historyDay
        : current.bufferDate === historyDay;
      if (discovery && targetMatches) {
        set((state) => ({
          historyError: 'Replay cache no longer matches discovery.',
          historySourcesHydrating: false,
          historySourcesHydratingDate: null,
          ...(state.mode === 'playing' ? {} : {
            bufferFrames: [],
            replayDatasetSession: null,
            replayCoverage: null,
          }),
          ...(staging ? {
            stagedReplayFrames: [],
            stagedReplayDatasetSession: null,
            stagedReplayCoverage: null,
          } : {}),
        }));
      } else {
        set({ historySourcesHydrating: false, historySourcesHydratingDate: null });
      }
      return null;
    }
    let hydratedFrames = [];
    let replaySource = null;
    set((state) => {
      const staging = state.replayDateSelection?.tradingDate === historyDay;
      const targetDate = staging ? state.stagedReplayDate : state.bufferDate;
      if (state._historyGeneration !== historyGeneration || targetDate !== historyDay) {
        return {
          historySourcesHydrating: false,
          historySourcesHydratingDate: null,
        };
      }
      const datasetFrames = filterReplaySessionFrames(
        replayDatasetFrames(datasetSession),
      );
      const targetFrames = staging ? state.stagedReplayFrames : state.bufferFrames;
      hydratedFrames = mergeRecoveredReplayFrames(targetFrames, datasetFrames);
      const coverage = supplementReplayCoverageWithLocalDatasets(
        datasetSession.coverage,
        replayCoverageDatasets(datasetSession, hydratedFrames),
      );
      const sourceIsActiveDay = state.mode === 'playing'
        && state.replaySource?.tradingDate === historyDay;
      replaySource = sourceIsActiveDay ? {
        chainSnaps: mergeReplayChainSnaps([], hydratedFrames),
        greeks: {},
        indexData: {},
        miPosts: liveReplayMiPosts(state, historyDay),
        tradingDate: historyDay,
        datasets: datasetSession.datasets,
        supplementalDatasets: datasetSession.supplementalDatasets,
        coverage,
        session: datasetSession.session,
      } : null;
      return {
        historySourcesHydrating: false,
        historySourcesHydratingDate: null,
        ...(staging ? {
          stagedReplayFrames: hydratedFrames,
          stagedReplayDatasetSession: datasetSession,
          stagedReplayCoverage: coverage,
          stagedReplayDatasetCacheCandidate: datasetSession,
        } : {
          bufferFrames: hydratedFrames,
          replayDatasetSession: datasetSession,
          replayCoverage: coverage,
          replayDatasetCacheCandidate: datasetSession,
          ...(replaySource ? { replaySource } : {}),
        }),
      };
    });
    const next = get();
    if (replaySource && next.mode === 'playing' && next.index >= 0) {
      applyCutoff(next.frames[next.index], replaySource, next.replayCutoffMs);
    }
    return datasetSession;
  },

  selectReplayHistoryDate: async (tradingDate, {
    partialWidgetIds = [], discovery = null,
  } = {}) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(tradingDate || '')) return 0;
    set((state) => {
      const bufferMatchesTarget = state.bufferDate === tradingDate
        && state.bufferFrames.length > 0;
      const sessionMatchesTarget = bufferMatchesTarget
        && state.replayDatasetSession?.tradingDate === tradingDate;
      return {
        replayDateSelection: {
          tradingDate,
          bufferFrames: state.bufferFrames,
          bufferDate: state.bufferDate,
          historyHydrated: state.historyHydrated,
          replayDatasetSession: state.replayDatasetSession,
          replayCoverage: state.replayCoverage,
          replayDatasetCacheCandidate: state.replayDatasetCacheCandidate,
          previousReplayDateSelection: state.replayDateSelection,
          previousStagedReplayFrames: state.stagedReplayFrames,
          previousStagedReplayDate: state.stagedReplayDate,
          previousStagedReplayDatasetSession: state.stagedReplayDatasetSession,
          previousStagedReplayCoverage: state.stagedReplayCoverage,
          previousStagedReplayDatasetCacheCandidate: state.stagedReplayDatasetCacheCandidate,
        },
        stagedReplayFrames: bufferMatchesTarget ? state.bufferFrames : [],
        stagedReplayDate: tradingDate,
        stagedReplayDatasetSession: sessionMatchesTarget ? state.replayDatasetSession : null,
        stagedReplayCoverage: sessionMatchesTarget ? state.replayCoverage : null,
        stagedReplayDatasetCacheCandidate: sessionMatchesTarget
          ? state.replayDatasetCacheCandidate
          : null,
        historyHydrated: false,
        historyHydrating: false,
        historyHydratingRequestKey: null,
        historySourcesHydrating: false,
        historySourcesHydratingDate: null,
        historyIntentChecking: false,
        historyError: null,
        _historyGeneration: state._historyGeneration + 1,
      };
    });
    return get().hydrateReplayHistory({
      partialWidgetIds,
      tradingDate,
      discovery,
      loadDatasetRecords: true,
    });
  },

  discardStagedReplayHistoryDate: () => {
    const selection = get().replayDateSelection;
    if (!selection) return false;
    set({
      historyHydrated: selection.historyHydrated,
      replayDatasetSession: selection.replayDatasetSession,
      replayCoverage: selection.replayCoverage,
      replayDatasetCacheCandidate: selection.replayDatasetCacheCandidate,
      stagedReplayDatasetSession: selection.previousStagedReplayDatasetSession || null,
      stagedReplayCoverage: selection.previousStagedReplayCoverage || null,
      stagedReplayDatasetCacheCandidate: selection.previousStagedReplayDatasetCacheCandidate || null,
      stagedReplayFrames: selection.previousStagedReplayFrames || [],
      stagedReplayDate: selection.previousStagedReplayDate || null,
      replayDateSelection: selection.previousReplayDateSelection || null,
      historyHydrating: false,
      historyHydratingRequestKey: null,
      historySourcesHydrating: false,
      historySourcesHydratingDate: null,
      historyError: null,
    });
    return true;
  },

  commitStagedReplayHistoryDate: () => {
    const state = get();
    if (!state.replayDateSelection || !state.stagedReplayDate) return false;
    set({
      bufferFrames: state.stagedReplayFrames,
      bufferDate: state.stagedReplayDate,
      replayDatasetSession: state.stagedReplayDatasetSession,
      replayCoverage: state.stagedReplayCoverage,
      replayDatasetCacheCandidate: state.stagedReplayDatasetCacheCandidate,
      stagedReplayDatasetSession: null,
      stagedReplayCoverage: null,
      stagedReplayDatasetCacheCandidate: null,
      stagedReplayFrames: [],
      stagedReplayDate: null,
      replayDateSelection: null,
    });
    return true;
  },

  activateReplayHistoryDate: ({ partialWidgetIds = null } = {}) => {
    const state = get();
    const staging = Boolean(state.replayDateSelection);
    const tradingDate = staging ? state.stagedReplayDate : state.bufferDate;
    const activeSameDay = state.mode === 'playing'
      && state.replayHistoryActive
      && state.replaySource?.tradingDate === tradingDate;
    const activeCutoffMs = activeSameDay ? state.replayCutoffMs : null;
    const activeWasPlaying = activeSameDay && state.playing;
    const currentDay = etDateKey(Date.now());
    const stagedCurrentDay = staging && state.stagedReplayDate === currentDay;
    const activeMinute = Math.floor(Date.now() / BUFFER_MINUTE_MS);
    const currentDayBufferMatchesSelection = stagedCurrentDay
      && state.bufferDate === state.stagedReplayDate;
    const completedFrames = (frames) => tradingDate === currentDay
      ? frames.filter((frame) => Math.floor(frame.t / BUFFER_MINUTE_MS) < activeMinute)
      : frames;
    const selectedSourceFrames = staging
      ? mergeMinuteFrames(
        state.stagedReplayFrames,
        currentDayBufferMatchesSelection ? state.bufferFrames : [],
      )
      : state.bufferFrames;
    const canonicalBufferFrames = filterReplaySessionFrames(latestTradeDayOnly(
      mergeMinuteFrames(
        activeSameDay ? state.frames.filter((frame) => !frame.preservedCutoff) : [],
        selectedSourceFrames.filter((frame) => !frame.preservedCutoff),
      ),
    ));
    const refreshedFrames = staging || activeSameDay
      ? completedFrames(canonicalBufferFrames)
      : canonicalBufferFrames;
    const { frames: playbackFrames, index: preservedIndex } = preserveCutoffFrame(
      refreshedFrames,
      activeSameDay ? state.frames : [],
      activeCutoffMs,
    );
    if (!playbackFrames.length) return false;
    const targetIndex = activeSameDay ? preservedIndex : playbackFrames.length - 1;
    const session = state.stagedReplayDatasetSession || state.replayDatasetSession;
    const coverage = state.stagedReplayCoverage || state.replayCoverage;
    const liveStoreState = state.liveStoreState || captureLiveSnapshot().liveStoreState;
    const currentDayGreeks = tradingDate === currentDay
      ? (getGreeksDataState(pendingGreeksScratch) || liveStoreState.greeks || {})
      : {};
    const replaySource = {
      ...(session || {}),
      chainSnaps: activeSameDay
        ? mergeSameDayReplayChainSnaps(
          state.replaySource.chainSnaps,
          canonicalBufferFrames,
          tradingDate,
        )
        : mergeReplayChainSnaps([], canonicalBufferFrames),
      greeks: activeSameDay && tradingDate !== currentDay
        ? (state.replaySource.greeks || {})
        : {
          greekExposures: currentDayGreeks.greekExposures,
          gexTrendStrength: currentDayGreeks.gexTrendStrength,
          adaptiveTopOptions: currentDayGreeks.adaptiveTopOptions,
          adaptiveDynamicLevels: currentDayGreeks.adaptiveDynamicLevels,
        },
      indexData: activeSameDay ? (state.replaySource.indexData || {}) : {},
      miPosts: liveReplayMiPosts(state, tradingDate),
      tradingDate,
      datasets: session?.datasets,
      supplementalDatasets: session?.supplementalDatasets,
      coverage,
      session: session?.session,
    };
    if (!get().loadFixture({
      frames: playbackFrames,
      replaySource,
      liveStoreState,
      partialWidgetIds: partialWidgetIds || state.partialWidgetIds,
      keepAutoBuffer: true,
      replayHistoryActive: true,
    })) return false;
    set({
      bufferFrames: canonicalBufferFrames,
      bufferDate: replaySource.tradingDate,
      ...(staging ? {
        stagedReplayFrames: canonicalBufferFrames,
        stagedReplayDate: replaySource.tradingDate,
        stagedReplayDatasetSession: session,
        stagedReplayCoverage: coverage,
        stagedReplayDatasetCacheCandidate: state.stagedReplayDatasetCacheCandidate,
        replayDateSelection: state.replayDateSelection,
      } : {}),
    });
    const gapFilled = state.replayGap && refreshedFrames.some((frame) => (
      Math.floor(frame.t / BUFFER_MINUTE_MS)
        === Math.floor(state.replayGap.t / BUFFER_MINUTE_MS)
    ));
    get().seek(targetIndex, { gap: activeSameDay && !gapFilled ? state.replayGap : null });
    if (activeWasPlaying) get().play();
    return true;
  },

  startAutoBuffer: () => {
    if (get()._bufferId) return;

    const capture = () => {
      // ReplayControls calls startAutoBuffer() on mount, which captures
      // synchronously -- at that point rowsBySym is {} and spot is null. Without
      // this the head of the scrub bar is a blank minute presented as real data,
      // and applyCutoff would then assert hasLiveOptionsData on it.
      //
      // The date test is the other half: since TD065 the chain survives the close,
      // so at the next open "we have rows" no longer means "we have TODAY's rows".
      // Recording one anyway would stamp the previous session's chain with this
      // minute and make it the head of the new day's scrub timeline.
      captureAutoBufferFrame(set, get);
    };
    const scheduleNext = () => {
      const delay = BUFFER_MINUTE_MS - (Date.now() % BUFFER_MINUTE_MS) + 500;
      set({
        _bufferId: setTimeout(() => {
          capture();
          // The tick keeps running after the close (capture() no-ops), which is
          // what gives the session-close extension a trigger without a second
          // timer: the last minutes of the day land in the buffer while the
          // user is already replaying, so nothing else would ever unfreeze them.
          get().extendReplayRange();
          scheduleNext();
        }, delay),
      });
    };

    capture();
    scheduleNext();
  },

  pauseAutoBuffer: () => {
    const { _bufferId } = get();
    if (_bufferId) clearTimeout(_bufferId);
    set({ _bufferId: null });
  },

  // partialWidgetIds is supplied by the caller rather than imported from
  // widgetRegistry: the registry pulls in every widget component, several of
  // which import this store, so importing it here would close an import cycle.
  // ReplayControls passes PARTIAL_REPLAY_WIDGET_IDS.
  enterBufferReview: ({ partialWidgetIds = [] } = {}) => {
    captureAutoBufferFrame(set, get);
    const frames = filterReplaySessionFrames(latestTradeDayOnly(get().bufferFrames));
    if (!frames.length) return false;
    const datasetSession = get().replayDatasetSession;
    const { liveStoreState, replaySource: capturedSource } = captureLiveSnapshot();
    const tradingDate = datasetSession?.tradingDate || get().bufferDate;
    const targetDateChainSnaps = (capturedSource.chainSnaps || []).filter(
      (snapshot) => etDateKey(snapshot.t) === tradingDate,
    );
    const sourceFallback = datasetSession ? {
      greeks: {},
      indexData: {},
    } : {
      greeks: capturedSource.greeks || {},
      indexData: capturedSource.indexData || {},
    };
    if (!get().loadFixture({
      frames: [...frames],
      replaySource: {
        ...(datasetSession || {}),
        chainSnaps: mergeReplayChainSnaps(targetDateChainSnaps, frames),
        ...sourceFallback,
        miPosts: liveReplayMiPosts(get(), tradingDate),
        tradingDate,
        datasets: datasetSession?.datasets,
        supplementalDatasets: datasetSession?.supplementalDatasets,
        coverage: datasetSession
          ? supplementReplayCoverageWithLocalDatasets(
            datasetSession.coverage,
            replayCoverageDatasets(datasetSession, frames),
          )
          : null,
        session: datasetSession?.session,
      },
      liveStoreState,
      partialWidgetIds,
      keepAutoBuffer: true,
      replayHistoryActive: true,
    })) return false;
    get().seek(frames.length - 1);
    return true;
  },

  // The scrub range is deliberately frozen at replay entry: appending minutes
  // under an active slider moves the thumb away from the position the user
  // chose. That trade-off only earns its keep while capture is still running.
  // Once the session closes the range is final, so keeping it frozen just hides
  // the last minutes of the day behind an exit/re-enter cycle.
  extendReplayRange: () => {
    const { mode, frames, bufferFrames, replayHistoryActive, replaySource } = get();
    if (mode !== 'playing' || !replayHistoryActive || !frames.length) return false;
    if (isDuringMarketHours(Date.now())) return false;

    const lastT = frames[frames.length - 1].t;
    const replayDay = etDateKey(lastT);
    const added = filterReplaySessionFrames(latestTradeDayOnly(bufferFrames))
      .filter((frame) => (
        frame.t > lastT && etDateKey(frame.t) === replayDay && isValidFrame(frame)
      ));
    if (!added.length) return false;

    set({
      frames: [...frames, ...added.map(normalizeReplayFrame)],
      replaySource: {
        ...replaySource,
        chainSnaps: mergeReplayChainSnaps(replaySource?.chainSnaps, added),
        miPosts: liveReplayMiPosts(get(), replaySource?.tradingDate || replayDay),
      },
    });
    return true;
  },

  hydrateRecovery: async () => {
    try {
      const checkpoint = await loadPlaybackCheckpoint();
      const frames = Array.isArray(checkpoint?.frames) ? checkpoint.frames : [];
      if (!frames.length) {
        set({ recoveryAvailable: false, recoverySummary: null });
        return null;
      }
      set({
        recoveryAvailable: true,
        recoverySummary: summarizeCheckpoint(checkpoint),
        checkpointError: null,
      });
      return checkpoint;
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      set({ checkpointError: message || 'Checkpoint load failed.' });
      return null;
    }
  },

  discardCheckpoint: async ({ clearFrames = false } = {}) => {
    try {
      await clearPlaybackCheckpoint();
      set((s) => ({
        recoveryAvailable: false,
        recoverySummary: null,
        recoveredFromCheckpoint: clearFrames ? false : s.recoveredFromCheckpoint,
        checkpointError: null,
        ...(clearFrames ? {
          frames: [],
          _recordingStartedAt: null,
          _recordingSessionId: null,
          lastSavedFilename: null,
          lastTrimmedFilename: null,
          saveError: null,
        } : {}),
      }));
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      set({ checkpointError: message || 'Checkpoint cleanup failed.' });
      return false;
    }
  },

  restoreCheckpoint: async () => {
    const checkpoint = await get().hydrateRecovery();
    const frames = Array.isArray(checkpoint?.frames) ? checkpoint.frames : [];
    if (!frames.length) return false;
    get().pause();
    useWebSocketDataStore.getState().setReplayActive(false);
    set({
      mode: 'off',
      frames,
      index: -1,
      playing: false,
      saving: false,
      saveError: null,
      lastSavedFilename: null,
      lastTrimmedFilename: null,
      recoveredFromCheckpoint: true,
      recoveryAvailable: false,
      recoverySummary: null,
      checkpointError: null,
      _recordingStartedAt: checkpoint.recordedAt ?? frames[0]?.t ?? Date.now(),
      _recordingSessionId: checkpoint.sessionId ?? `recovered-${Date.now()}`,
    });
    return true;
  },

  persistCheckpoint: async (framesOverride) => {
    const state = get();
    const frames = framesOverride ?? state.frames;
    if (!Array.isArray(frames) || !frames.length) return false;
    const sessionId = state._recordingSessionId ?? `rec-${Date.now()}`;
    const recordedAt = state._recordingStartedAt ?? frames[0]?.t ?? Date.now();
    const checkpoint = makeCheckpointEnvelope({ sessionId, recordedAt, frames });
    const checkpointSeq = state._checkpointSeq + 1;
    set({ checkpointing: true, checkpointError: null, _checkpointSeq: checkpointSeq });
    try {
      await savePlaybackCheckpoint(checkpoint);
      if (get()._checkpointSeq !== checkpointSeq) return true;
      set({
        checkpointing: false,
        checkpointError: null,
        recoverySummary: summarizeCheckpoint(checkpoint),
      });
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      if (get()._checkpointSeq !== checkpointSeq) return false;
      set({ checkpointing: false, checkpointError: message || 'Checkpoint save failed.' });
      return false;
    }
  },

  startRecording: () => {
    if (get()._recId) return;
    const startedAt = Date.now();
    const sessionId = `rec-${startedAt}`;
    useWebSocketDataStore.getState().setReplayActive(false);
    set({
      mode: 'recording',
      frames: [],
      saving: false,
      saveError: null,
      lastSavedFilename: null,
      lastTrimmedFilename: null,
      checkpointing: false,
      checkpointError: null,
      recoveredFromCheckpoint: false,
      recoveryAvailable: false,
      recoverySummary: null,
      _recordingStartedAt: startedAt,
      _recordingSessionId: sessionId,
      _checkpointSeq: 0,
    });
    const tick = () => {
      const nextFrames = [...get().frames, captureFrame()];
      set({ frames: nextFrames });
      void get().persistCheckpoint(nextFrames);
    };
    tick();
    set({ _recId: setInterval(tick, REC_CADENCE_MS) });
  },

  saveRecording: () => {
    const { frames, saving, _recordingStartedAt } = get();
    if (saving || !frames.length) return { ok: false, count: frames.length };

    set({ saving: true, saveError: null });
    try {
      const stamp = new Date().toISOString().slice(0, 16).replace(/[:T]/g, '-');
      const filename = `spx-fixture-${stamp}.json`;
      const fixture = {
        version: FIXTURE_VERSION,
        recordedAt: _recordingStartedAt ?? Date.now(),
        cadenceMs: REC_CADENCE_MS,
        frames,
      };
      const blob = buildFixtureBlob(fixture);
      const trimmedFixture = trimPlaybackFixture(fixture, { mode: 'full' });
      const trimmedBlob = buildFixtureBlob(trimmedFixture);
      const trimmedFullFilename = trimmedFilename(filename, '-trimmed-full');
      downloadBlob(blob, filename);
      downloadBlob(trimmedBlob, trimmedFullFilename);
      set({
        saving: false,
        saveError: null,
        lastSavedFilename: filename,
        lastTrimmedFilename: trimmedFullFilename,
        checkpointing: false,
        checkpointError: null,
        recoveredFromCheckpoint: false,
        recoveryAvailable: false,
        recoverySummary: null,
        _recordingStartedAt: null,
        _recordingSessionId: null,
      });
      void clearPlaybackCheckpoint().catch((err) => {
        const message = err instanceof Error ? err.message : String(err);
        set({ checkpointError: message || 'Checkpoint cleanup failed.' });
      });
      return { ok: true, count: frames.length, filename };
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      set({
        saving: false,
        saveError: message || 'Unknown save error.',
        lastSavedFilename: null,
        lastTrimmedFilename: null,
      });
      return { ok: false, count: frames.length, error: message };
    }
  },

  stopRecording: () => {
    const { _recId, frames } = get();
    if (_recId) clearInterval(_recId);
    set({ _recId: null, mode: 'off' });
    if (!frames.length) return { ok: false, count: 0 };
    return get().saveRecording();
  },

  loadFixture: (fixture) => {
    const frames = Array.isArray(fixture) ? fixture : fixture?.frames;
    if (!Array.isArray(frames) || !frames.length || !frames.every(isValidFrame)) return false;
    const alreadyPlaying = get().mode === 'playing';
    if (!alreadyPlaying) clearPendingLiveQueues();
    const existingPendingLiveUpdates = alreadyPlaying
      ? get().pendingLiveUpdates
      : emptyPendingLiveUpdates();
    get().pause();
    if (!fixture?.keepAutoBuffer) get().pauseAutoBuffer();
    const liveStoreState =
      get().liveStoreState || fixture?.liveStoreState || captureLiveSnapshot().liveStoreState;
    if (!pendingGreeksScratch) initializePendingGreeksScratch(liveStoreState.greeks);
    useWebSocketDataStore.getState().beginReplay();
    const replaySource = fixture?.replaySource
      ? {
        ...fixture.replaySource,
        chainSnaps: (fixture.replaySource.chainSnaps || []).map(stampImportedChainSnapshot),
      }
      : {
        chainSnaps: buildFixtureChainSnaps(frames),
        greeks: frames[frames.length - 1]?.greeks || {},
        indexData: frames[frames.length - 1]?.indexData || {},
      };
    set({
      mode: 'playing',
      frames: frames.map(normalizeReplayFrame),
      index: -1,
      saving: false,
      saveError: null,
      lastSavedFilename: null,
      lastTrimmedFilename: null,
      checkpointing: false,
      checkpointError: null,
      recoveredFromCheckpoint: false,
      recoveryAvailable: false,
      recoverySummary: null,
      _recordingStartedAt: null,
      _recordingSessionId: null,
      _checkpointSeq: 0,
      replayCutoffMs: null,
      replaySource,
      replayCoverage: replaySource?.coverage || null,
      replayDatasetSession: replaySource?.datasets ? replaySource : null,
      replayDatasetCacheCandidate: null,
      stagedReplayCoverage: null,
      stagedReplayDatasetSession: null,
      stagedReplayDatasetCacheCandidate: null,
      stagedReplayFrames: [],
      stagedReplayDate: null,
      replayDateSelection: null,
      replayGap: null,
      replayHistoryActive: Boolean(fixture?.replayHistoryActive),
      partialWidgetIds: fixture?.partialWidgetIds || [],
      liveStoreState,
      pendingLiveUpdates: existingPendingLiveUpdates,
    });
    get().seek(0);
    return true;
  },

  hydrateReplayDatasetsFromRecovery: async (
    payload,
    { coverage = null, cachedSession = null, signal = null } = {},
  ) => {
    const historyGeneration = get()._historyGeneration;
    const cancelled = () => ({
      recovered: 0,
      remainingGaps: countReplayGaps(get().bufferFrames),
      skipped: 'cancelled',
    });
    if (signal?.aborted) return cancelled();
    if (!get().historyHydrated) {
      await get().hydrateReplayHistory({ tradingDate: payload.tradingDate, discovery: payload });
    }
    if (signal?.aborted) return cancelled();
    if (get()._historyGeneration !== historyGeneration) {
      return {
        recovered: 0,
        remainingGaps: countReplayGaps(get().bufferFrames),
        skipped: 'replay_exited',
      };
    }
    const expectedSession = { ...payload, coverage: coverage || payload.coverage };
    const stored = cachedSession
      || await loadReplayDatasetSession(payload.tradingDate).catch(() => null);
    if (signal?.aborted) return cancelled();
    const cached = replayDatasetSessionMatchesCoverage(stored, expectedSession)
      || (payload.requestedRange
        && replayDatasetSessionHasCompatibleProvenance(stored, expectedSession))
      ? stored
      : null;
    if (get()._historyGeneration !== historyGeneration) {
      return {
        recovered: 0,
        remainingGaps: countReplayGaps(get().bufferFrames),
        skipped: 'replay_exited',
      };
    }
    const state = get();
    const live = state.liveStoreState || captureLiveSnapshot().liveStoreState;
    const staging = state.replayDateSelection?.tradingDate === payload.tradingDate;
    const localDatasets = normalizeLocalReplayDatasets({
      tradingDate: payload.tradingDate,
      indexData: staging
        ? useWebSocketDataStore.getState().indexData
        : (state.replaySource?.indexData || useWebSocketDataStore.getState().indexData),
      greeks: live.greeks,
      miPosts: live.mi?.recentFrontendPosts || [],
    });
    const serverAndCache = mergeReplayDatasets(cached?.datasets, payload.datasets);
    const datasets = mergeReplayDatasets(serverAndCache, localDatasets);
    const supplementalDatasets = mergeReplayDatasets(
      cached?.supplementalDatasets, localDatasets,
    );
    const cachedAndLocalDatasets = mergeReplayDatasets(
      cached?.datasets, supplementalDatasets,
    );
    const hydration = await get().hydrateReplayFramesFromRecovery(datasets.chain || [], {
      tradingDate: payload.tradingDate,
      fallbackFrames: replayDatasetAnchorFrames(datasets),
      signal,
    });
    if (signal?.aborted) return cancelled();
    if (get()._historyGeneration !== historyGeneration) {
      return hydration.skipped === 'persist_failed'
        ? hydration
        : { ...hydration, skipped: 'replay_exited' };
    }
    if (hydration.skipped) return hydration;
    const next = get();
    const targetFrames = staging
      ? next.stagedReplayFrames
      : next.bufferFrames;
    const projectionCoverage = supplementReplayCoverageWithLocalDatasets(
      coverage || payload.coverage,
      mergeReplayDatasets(cachedAndLocalDatasets, {
        chain: replayChainRecordsFromFrames(targetFrames),
      }),
    );
    const session = {
      schemaVersion: 2,
      tradingDate: payload.tradingDate,
      requestedRange: null,
      generatedAt: payload.generatedAt,
      session: payload.session,
      coverage: projectionCoverage,
      datasets,
      datasetRecordCounts: Object.fromEntries(
        Object.entries(datasets).map(([dataset, values]) => [dataset, values.length]),
      ),
      supplementalDatasets,
    };
    const replaySource = {
      chainSnaps: mergeReplayChainSnaps([], targetFrames || []),
      greeks: {},
      indexData: {},
      miPosts: liveReplayMiPosts(next, payload.tradingDate),
      tradingDate: payload.tradingDate,
      datasets,
      supplementalDatasets,
      coverage: projectionCoverage,
      session: payload.session,
    };
    if (signal?.aborted) return cancelled();
    set(staging ? {
      historyError: null,
      stagedReplayCoverage: projectionCoverage,
      stagedReplayDatasetSession: session,
      stagedReplayDatasetCacheCandidate: null,
    } : {
      historyError: null,
      replaySource,
      replayCoverage: projectionCoverage,
      replayDatasetSession: session,
      replayDatasetCacheCandidate: null,
    });
    if (!staging && next.mode === 'playing' && next.index >= 0) {
      applyCutoff(next.frames[next.index], replaySource, next.replayCutoffMs);
    }
    let persistFailed = false;
    try {
      await persistReplayDatasetSession(session, { signal });
    } catch (err) {
      if (signal?.aborted) return cancelled();
      const message = err instanceof Error ? err.message : String(err);
      if (get()._historyGeneration === historyGeneration) {
        set({ historyError: message || 'Replay dataset save failed.' });
      }
      persistFailed = true;
    }
    return {
      ...hydration,
      ...(persistFailed ? { skipped: 'persist_failed' } : {}),
    };
  },

  hydrateReplayFramesFromRecovery: async (
    apiFrames = [],
    { tradingDate, fallbackFrames = [], signal = null } = {},
  ) => {
    const historyGeneration = get()._historyGeneration;
    const cancelled = () => ({
      recovered: 0,
      remainingGaps: countReplayGaps(get().bufferFrames),
      skipped: 'cancelled',
    });
    if (signal?.aborted) return cancelled();
    if (!get().historyHydrated) {
      await get().hydrateReplayHistory();
    }
    if (signal?.aborted) return cancelled();
    if (get()._historyGeneration !== historyGeneration) {
      return {
        recovered: 0,
        remainingGaps: countReplayGaps(get().bufferFrames),
        skipped: 'replay_exited',
      };
    }
    const recoveredChain = (apiFrames || [])
      .map(recoveredApiFrameToBufferFrame)
      .filter(Boolean);
    const recovered = recoveredChain.length
      ? recoveredChain
      : (fallbackFrames || []).filter(isValidFrame);
    const state = get();
    const recoveredDay = tradingDate
      || (recovered[0] ? etDateKey(recovered[0].t) : etDateKey(Date.now()));
    const staging = state.replayDateSelection?.tradingDate === recoveredDay;
    const targetFrames = staging ? state.stagedReplayFrames : state.bufferFrames;
    const targetDate = staging ? state.stagedReplayDate : state.bufferDate;
    const recoveredForDay = recovered.filter((frame) => etDateKey(frame.t) === recoveredDay);
    const localForDay = (targetFrames || []).filter((frame) => (
      Number.isFinite(frame?.t) && etDateKey(frame.t) === recoveredDay
    ));
    // The buffer's own day always merges. Any other day has to carry frames
    // AND be one of two cases: today (the roll-forward once the session
    // starts), or an empty buffer -- a fresh browser has no captured session to
    // protect, and hydrateReplayHistory stamps it with the current calendar
    // date, so on a weekend the server's stored session is both authoritative
    // and the only thing there is to replay. A third day over a non-empty
    // buffer must never land here: it would write under the wrong IndexedDB
    // key and make countReplayGaps span the overnight close (~1500 phantom
    // minutes).
    const localBufferEmpty = !(targetFrames || []).length;
    const mergeable = recoveredDay === targetDate || (
      recoveredForDay.length > 0
      && (recoveredDay === etDateKey(Date.now()) || localBufferEmpty)
    );
    if (!mergeable) {
      return {
        recovered: 0,
        remainingGaps: countReplayGaps(localForDay),
        skipped: 'prior_session',
      };
    }
    const existingMinutes = new Set(
      localForDay.map((frame) => Math.floor(frame.t / BUFFER_MINUTE_MS)),
    );
    const merged = mergeRecoveredReplayFrames(localForDay, recoveredForDay);
    const newlyRecovered = merged.filter((frame) => (
      frame.partial === true
      && !existingMinutes.has(Math.floor(frame.t / BUFFER_MINUTE_MS))
    ));
    if (signal?.aborted) return cancelled();
    set(staging ? {
      stagedReplayFrames: merged,
      stagedReplayDate: recoveredDay,
    } : {
      bufferFrames: merged,
      bufferDate: recoveredDay,
    });
    const replayDay = state.frames[0]
      ? etDateKey(state.frames[0].t)
      : (Number.isFinite(state.replayCutoffMs) ? etDateKey(state.replayCutoffMs) : null);
    // Recorder fixture replay is also mode === 'playing'. Replacing its frames
    // with the live buffer (or []) is the same footgun extendReplayRange guards.
    if (
      !staging
      && state.mode === 'playing'
      && state.replayHistoryActive
      && replayDay === recoveredDay
    ) {
      const cutoffMs = state.replayCutoffMs;
      const replayGap = state.replayGap;
      const canonicalFrames = filterReplaySessionFrames(latestTradeDayOnly(merged));
      const { frames, index: restoreIndex } = preserveCutoffFrame(
        canonicalFrames,
        state.frames,
        cutoffMs,
      );
      const chainSnaps = mergeReplayChainSnaps(
        state.replaySource?.chainSnaps || [],
        canonicalFrames,
      );
      set({
        frames,
        index: -1,
        replaySource: {
          ...state.replaySource,
          chainSnaps,
          // Recovery extends the timeline past where `replaySource` was cloned.
          // Without re-reading the live bars the chain advances to the recovered
          // end while the price chart stays pinned at the last minute captured
          // before the disconnect. Recovered frames carry no bars themselves, so
          // this fills only from what the live store genuinely has, and only for
          // the day being replayed.
          indexData: liveReplayIndexData(state.replaySource?.indexData, recoveredDay),
        },
      });
      if (frames.length) {
        const gapFilled = replayGap && frames.some((frame) => (
          Math.floor(frame.t / BUFFER_MINUTE_MS) === Math.floor(replayGap.t / BUFFER_MINUTE_MS)
        ));
        get().seek(restoreIndex, { gap: gapFilled ? null : replayGap });
      }
    }
    let persisted = newlyRecovered.length;
    let persistFailed = false;
    try {
      await persistReplaySnapshots(recoveredDay, newlyRecovered, {
        activateDay: !staging,
        signal,
      });
    } catch (err) {
      if (signal?.aborted) return cancelled();
      const message = err instanceof Error ? err.message : String(err);
      if (get()._historyGeneration === historyGeneration) {
        set({ historyError: message || 'Replay recovery save failed.' });
      }
      persisted = 0;
      persistFailed = true;
    }
    return {
      recovered: persisted,
      remainingGaps: countReplayGaps(merged),
      ...(persistFailed ? { skipped: 'persist_failed' } : {}),
    };
  },

  seek: (i, { gap = null } = {}) => {
    const { frames, index, replaySource, replayGap, replayCutoffMs } = get();
    if (!frames.length) return;
    const idx = Math.max(0, Math.min(frames.length - 1, i));
    const sameGap = (replayGap?.t ?? null) === (gap?.t ?? null);
    if (idx === index && sameGap) return;
    applyCutoff(frames[idx], replaySource, replayCutoffMs);
    set({ index: idx, replayCutoffMs: frames[idx].t, replayGap: gap });
    useChartTimeWindowStore.getState().followReplayCutoff(frames[idx].t);
    useChartPriceWindowStore.getState().followReplayDate(etDateKey(frames[idx].t));
    if (get().replayHistoryActive) {
      void persistReplayState({
        active: true,
        day: etDateKey(frames[idx].t),
        cutoffMs: frames[idx].t,
      }).catch((err) => {
        const message = err instanceof Error ? err.message : String(err);
        set({ historyError: message || 'Replay position save failed.' });
      });
    }
  },

  seekMarker: (marker) => {
    if (!marker) return;
    if (marker.kind === 'gap') {
      const idx = lastFrameIndexBefore(get().frames, marker.t);
      if (idx < 0) return;
      get().seek(idx, {
        gap: {
          t: marker.t,
          startT: marker.gapStartT ?? marker.t,
          endT: marker.gapEndT ?? marker.t,
        },
      });
      return;
    }
    if (Number.isFinite(marker.frameIndex)) get().seek(marker.frameIndex);
  },

  play: () => {
    const { frames, playing, speed } = get();
    if (playing || !frames.length) return;
    set({ playing: true });
    const step = () => {
      const { index, frames: currentFrames, replayCutoffMs, replaySource } = get();
      const next = index + 1;
      if (next >= currentFrames.length) {
        get().pause();
        return;
      }
      applyCutoff(currentFrames[next], replaySource, replayCutoffMs);
      set({ index: next, replayCutoffMs: currentFrames[next].t, replayGap: null });
      useChartTimeWindowStore.getState().followReplayCutoff(currentFrames[next].t);
      useChartPriceWindowStore.getState().followReplayDate(etDateKey(currentFrames[next].t));
      if (get().replayHistoryActive) {
        void persistReplayState({
          active: true,
          day: etDateKey(currentFrames[next].t),
          cutoffMs: currentFrames[next].t,
        }).catch((err) => {
          const message = err instanceof Error ? err.message : String(err);
          set({ historyError: message || 'Replay position save failed.' });
        });
      }
    };
    set({ _playId: setInterval(step, REC_CADENCE_MS / Math.max(0.25, speed)) });
  },

  pause: () => {
    const { _playId } = get();
    if (_playId) clearInterval(_playId);
    set({ _playId: null, playing: false });
  },

  setSpeed: (speed) => {
    const wasPlaying = get().playing;
    get().pause();
    set({ speed });
    if (wasPlaying) get().play();
  },

  exitPlayback: () => {
    get().pause();
    checkedFramesBySnapshot = new WeakMap();
    useWebSocketDataStore.getState().setReplayActive(false);
    const {
      liveStoreState,
      pendingLiveUpdates,
      replayHistoryActive,
      historyExitPersisting,
      _historyGeneration,
      _historyExitPersistenceGeneration,
      stagedReplayCoverage,
      stagedReplayDatasetSession,
      stagedReplayDatasetCacheCandidate,
      stagedReplayFrames,
      stagedReplayDate,
      replayDateSelection,
    } = get();
    const exitGeneration = _historyGeneration + 1;
    const exitPersistenceGeneration = replayHistoryActive
      ? _historyExitPersistenceGeneration + 1
      : _historyExitPersistenceGeneration;
    const optionRows = [...pendingOptionRows.values()];
    const aggPatches = [...pendingAggPatches.values()];
    const liveSpot = pendingLiveSpot;
    const liveGreeks = getGreeksDataState(pendingGreeksScratch);
    const miSnapshots = [...pendingMiSnapshots];
    clearPendingLiveQueues();
    set({
      mode: 'off',
      frames: [],
      index: -1,
      saving: false,
      saveError: null,
      lastSavedFilename: null,
      lastTrimmedFilename: null,
      checkpointing: false,
      checkpointError: null,
      recoveredFromCheckpoint: false,
      recoveryAvailable: false,
      recoverySummary: null,
      _recordingStartedAt: null,
      _recordingSessionId: null,
      _checkpointSeq: 0,
      replayCutoffMs: null,
      replaySource: null,
      replayCoverage: null,
      replayDatasetSession: null,
      replayDatasetCacheCandidate: null,
      stagedReplayCoverage: null,
      stagedReplayDatasetSession: null,
      stagedReplayDatasetCacheCandidate: null,
      stagedReplayFrames: [],
      stagedReplayDate: null,
      replayDateSelection: null,
      historyHydrated: false,
      historyHydrating: false,
      historyHydratingRequestKey: null,
      historySourcesHydrating: false,
      historySourcesHydratingDate: null,
      historyIntentChecking: false,
      historyExitPersisting: historyExitPersisting || replayHistoryActive,
      _historyGeneration: exitGeneration,
      _historyExitPersistenceGeneration: exitPersistenceGeneration,
      replayGap: null,
      replayHistoryActive: false,
      partialWidgetIds: [],
      liveStoreState: null,
      pendingLiveUpdates: emptyPendingLiveUpdates(),
      ...(replayDateSelection ? {
        stagedReplayCoverage,
        stagedReplayDatasetSession,
        stagedReplayDatasetCacheCandidate,
        stagedReplayFrames,
        stagedReplayDate,
        replayDateSelection,
        historyHydrated: true,
      } : {}),
    });
    drainPendingLiveUpdates(
      liveStoreState,
      pendingLiveUpdates,
      optionRows,
      aggPatches,
      liveSpot,
      liveGreeks,
      miSnapshots,
    );
    const liveTradingDate = restoredLiveTradingDate();
    useChartTimeWindowStore.getState().endReplayWindow(liveTradingDate);
    useChartPriceWindowStore.getState().endReplayWindow(liveTradingDate);
    if (replayHistoryActive) {
      void persistReplayState(
        { active: false, day: etDateKey(Date.now()), cutoffMs: null },
        { immediate: true },
      )
        .then(() => set((state) => (
          state._historyExitPersistenceGeneration === exitPersistenceGeneration
            ? { historyExitPersisting: false }
            : {}
        )))
        .catch((err) => {
          const message = err instanceof Error ? err.message : String(err);
          set((state) => (
            state._historyExitPersistenceGeneration === exitPersistenceGeneration
              ? { historyError: message || 'Replay position save failed.' }
              : {}
          ));
        });
    }
    get().startAutoBuffer();
  },
}));

// A reload restores an active replay before the live MI history lands, and the
// restore schedules no later source rebuild that would pick it up.
useMarketIntelligenceStore.subscribe((mi, previous) => {
  if (mi.historicalFrontendPosts === previous.historicalFrontendPosts) return;
  const playback = usePlaybackStore.getState();
  if (playback.replaySource?.tradingDate === mi.historicalTradingDate) {
    playback.refreshReplayMiPosts();
  }
});

export default usePlaybackStore;
